import React from 'react';
import { formatNumber } from '../../common/lib/formatNumber';
import type { MotorAd, SearchKey } from '../../types';
import { SearchAd } from '../search/SearchAd';
import { useSearchAdClasses } from '../search/SearchAdClasses';
import type {
    BaseSearchAdProps,
    SearchAdLayout,
} from '../search/SearchAdTypes';
import { MotorHeading } from '../search/motor/MotorHeading';
import { MotorImage } from '../search/motor/MotorImage';
import { LegendaryAdImageOptions } from './LegendaryAdImageOptions';
import { LegendaryMotorPrimaryTag } from './LegendaryMotorPrimaryTag';
import { SecondaryTag } from './SecondayTag';

const SHOW_MILEAGE_FOR_SEARCH_KEYS = [
    'SEARCH_ID_CAR_USED',
    'SEARCH_ID_CAR_PARALLEL_IMPORT',
    'SEARCH_ID_CAR_TRUCK',
    'SEARCH_ID_CAR_TRUCK_ABROAD',
    'SEARCH_ID_MC_USED',
    'SEARCH_ID_MC_SNOWMOBILE',
    'SEARCH_ID_MC_SCOOTER',
    'SEARCH_ID_MC_ATV',
    'SEARCH_ID_CAR_MOBILE_HOME',
];

export const gridClasses = {
    mobile: {
        ad: '',
        imageWrapper: 'mb-8',
        image: 'rounded-8 border s-border',
        location: 'pr-44 order-first space-x-16 text-xs s-text-subtle',
        content: 'flex flex-col',
        heading: 'mb-8 pr-44 break-words',
        summary: 'mb-8 flex justify-between whitespace-nowrap font-bold',
        organisation: 'flex flex-col text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
        favorite: 'relative order-first',
        favoriteButton: 'absolute top-0 right-0',
    },
    desktop: {},
};

export const listClasses = {
    mobile: {
        ad: 'grid grid-cols-3 grid-rows-1 gap-x-16',
        imageWrapper: '',
        image: 'rounded-8 border s-border',
        location:
            'col-span-2 order-first pr-44 space-x-16 text-xs s-text-subtle',
        content: 'col-span-2 flex flex-col',
        heading: 'mb-8 pr-44 break-words',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'flex flex-col text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
        favorite: '',
        favoriteButton: '',
    },
    desktop: {},
};

type LegendaryMotorSearchAdProps = BaseSearchAdProps & {
    ad: MotorAd;
    imageSizes?: string;
    layout: SearchAdLayout;
    searchKey: SearchKey;
};

export function LegendaryMotorSearchAd(props: LegendaryMotorSearchAdProps) {
    const {
        ad,
        className = '',
        favorite,
        image,
        imageSizes,
        label,
        layout,
        searchKey,
        ...searchAdProps
    } = props;

    const layoutClasses = layout === 'grid' ? gridClasses : listClasses;
    const classes = useSearchAdClasses({
        mode: 'responsive',
        mobileClasses: layoutClasses.mobile,
        desktopClasses: layoutClasses.desktop,
    });

    const showMileage =
        ad.mileage != null &&
        ad.mileage > 0 &&
        SHOW_MILEAGE_FOR_SEARCH_KEYS.includes(searchKey);

    const soldLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'sold');
    const edialogLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'edialog');
    const promotedLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'fus');

    const isSold = ad.flags.includes('sold');
    const isPromoted = Boolean(label) || Boolean(promotedLabel);

    const legendaryPromotedLabel = label ? (
        <SecondaryTag className="order-first" render={label} />
    ) : (
        <SecondaryTag
            className="order-first"
            label={promotedLabel}
            type="info"
        />
    );

    return (
        <SearchAd
            adId={ad.id}
            className={`${classes.ad} ${className}`}
            favorite={layout === 'list' ? favorite : undefined}
            legendary
            {...searchAdProps}
        >
            <div className={classes.imageWrapper}>
                <LegendaryAdImageOptions>
                    <MotorImage
                        ad={ad}
                        image={image}
                        imageClass={classes.image}
                        sizes={imageSizes}
                    />
                </LegendaryAdImageOptions>
            </div>
            <div className={classes.content}>
                <MotorHeading ad={ad} className={classes.heading} />
                {favorite && layout === 'grid' && (
                    <div className={classes.favorite}>
                        <span className={classes.favoriteButton}>
                            {favorite()}
                        </span>
                    </div>
                )}
                {legendaryPromotedLabel}
                {soldLabel && !isPromoted && (
                    <SecondaryTag
                        className="order-first"
                        label={soldLabel}
                        type="warning"
                    />
                )}
                {edialogLabel && !isPromoted && (
                    <SecondaryTag
                        className="order-first"
                        label={edialogLabel}
                        type="positive"
                    />
                )}
                <div className={classes.location}>
                    {ad.flags?.includes('new_price') && (
                        <span className="s-text-subtle">Ny pris</span>
                    )}
                    {ad.location && <span>{ad.location}</span>}
                </div>
                {(ad.price || ad.mileage || ad.year) && (
                    <div className={classes.summary}>
                        {ad.year > 0 && <span>{ad.year}</span>}
                        {showMileage && (
                            <span>{formatNumber(ad.mileage as number)} km</span>
                        )}
                        {ad.price.amount > 0 && !isSold && (
                            <span>
                                {formatNumber(ad.price.amount)}
                                {ad.price.price_unit}
                            </span>
                        )}
                        {isSold && <span>Solgt</span>}
                    </div>
                )}
                <div className={classes.organisation}>
                    {ad.organisation_name && (
                        <span>{ad.organisation_name}</span>
                    )}
                    <BulletPointsRow ad={ad} />
                </div>
            </div>
            <LegendaryMotorPrimaryTag
                ad={ad}
                className={`sf-primary-tag-legendary ${classes.ribbon}`}
            />
        </SearchAd>
    );
}

const BulletPointsRow = ({ ad }: { ad: MotorAd }) => {
    function getBulletPointsText() {
        let output: string[] = [];
        if (ad.dealer_segment) output = output.concat(ad.dealer_segment);
        if (ad.warranty_duration)
            output = output.concat(ad.warranty_duration + ' mnd garanti');
        if (ad.flags?.includes('right_to_exchange'))
            output = output.concat('Bytterett');
        if (ad.service_documents) output = output.concat(ad.service_documents);
        if (ad.hours_used) output = output.concat(ad.hours_used + ' timer');

        return output.join(' • ');
    }

    const text = getBulletPointsText();

    return text ? <span>{text}</span> : null;
};
